import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Typography,
  Tabs,
  Space,
  Spin,
  App,
  Modal,
  Upload,
  Image,
  ColorPicker,
  Segmented,
  Switch,
  Tag
} from 'antd';
import {
  InfoCircleOutlined,
  BuildOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  UploadOutlined,
  BgColorsOutlined,
  BellOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import businessUnitService from '../services/businessUnitService';
import settingsService from '../services/settingsService';
import { fileToBase64 } from '../services/questionnaireService';
import PageHeader from './PageHeader';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import {
  DEFAULT_QUIZ_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  QUIZ_FONT_OPTIONS,
  DEFAULT_FONT_KEY,
  QUIZ_TEXT_SIZE_OPTIONS,
  QUIZ_TEXT_SIZE_LABELS,
  getFontStack,
  getTextSizePx
} from '../constants/quizDesign';
import { NOTIFICATION_CHANNELS, DEFAULT_NOTIFICATION_STRATEGY } from '../constants/notificationStrategy';

const { Title, Text } = Typography;
const { Option } = Select;

const FONT_SELECT_OPTIONS = QUIZ_FONT_OPTIONS.map((font) => ({
  label: font.label,
  value: font.value
}));

const TEXT_SIZE_SEGMENT_OPTIONS = QUIZ_TEXT_SIZE_OPTIONS.map((size) => ({
  label: QUIZ_TEXT_SIZE_LABELS[size],
  value: size
}));

const parseColorToRgb = (color) => {
  if (typeof color !== 'string') {
    return null;
  }

  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const normalized = hex.length === 3 ? hex.split('').map((char) => char + char).join('') : hex;
    const int = parseInt(normalized, 16);
    if (Number.isNaN(int)) {
      return null;
    }
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    };
  }

  const rgbMatch = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3])
    };
  }

  return null;
};

const getReadableTextColor = (color, fallback = '#000000') => {
  const rgb = parseColorToRgb(color);
  if (!rgb) {
    return fallback;
  }

  const luminance = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return luminance >= 150 ? '#000000' : '#ffffff';
};

const NotificationStrategySelector = ({ value = DEFAULT_NOTIFICATION_STRATEGY, onChange }) => {
  const strategy = Array.isArray(value) && value.length ? value : [...DEFAULT_NOTIFICATION_STRATEGY];

  const moveChannel = (channel, direction) => {
    const index = strategy.indexOf(channel);
    const targetIndex = index + direction;
    if (index === -1 || targetIndex < 0 || targetIndex >= strategy.length) {
      return;
    }
    const next = [...strategy];
    const [removed] = next.splice(index, 1);
    next.splice(targetIndex, 0, removed);
    onChange?.(next);
  };

  const toggleChannel = (channel, enabled) => {
    const next = strategy.filter(item => item !== channel);
    if (enabled) {
      next.push(channel);
      onChange?.(next);
      return;
    }

    if (strategy.length === 1) {
      return;
    }

    if (next.length === 0) {
      return;
    }

    onChange?.(next);
  };

  const activeChannels = strategy
    .map(key => NOTIFICATION_CHANNELS.find(channel => channel.value === key))
    .filter(Boolean);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Text type="secondary">Live order</Text>
        <div style={{ marginTop: 8 }}>
          {activeChannels.map((channel, index) => (
            <Tag key={channel.value} color={channel.accent || '#2E86AB'}>
              {`${index + 1}. ${channel.label}`}
            </Tag>
          ))}
        </div>
      </div>

      {NOTIFICATION_CHANNELS.map((channel) => {
        const isEnabled = strategy.includes(channel.value);
        const index = strategy.indexOf(channel.value);
        return (
          <Card key={channel.value} size="small" bodyStyle={{ padding: '12px 16px' }}>
            <Row align="middle" gutter={16}>
              <Col flex="auto">
                <Space align="start">
                  <Switch
                    checked={isEnabled}
                    onChange={(checked) => toggleChannel(channel.value, checked)}
                  />
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong>{channel.label}</strong>
                      {isEnabled && (
                        <Tag color={channel.accent || '#2E86AB'} style={{ margin: 0 }}>
                          #{index + 1}
                        </Tag>
                      )}
                    </div>
                    <Text type="secondary" style={{ display: 'block' }}>
                      {channel.description}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={!isEnabled || index === 0}
                    onClick={() => moveChannel(channel.value, -1)}
                  />
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={!isEnabled || index === strategy.length - 1}
                    onClick={() => moveChannel(channel.value, 1)}
                  />
                </Space>
              </Col>
            </Row>
          </Card>
        );
      })}
    </Space>
  );
};

const EditBusinessUnit = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [businessUnit, setBusinessUnit] = useState(null);
  const [availableOptions, setAvailableOptions] = useState({
    industries: [],
    countries: [],
    regions: [],
    managingGroups: [],
    departments: []
  });
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [addModalType, setAddModalType] = useState('');
  const [newItemValue, setNewItemValue] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [appearanceDesignTab, setAppearanceDesignTab] = useState('font');

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');
  const isEdit = mode === 'edit' && id;

  console.log('EditBusinessUnit render - id:', id, 'mode:', mode, 'isEdit:', isEdit);

  useEffect(() => {
    console.log('EditBusinessUnit useEffect - id:', id, 'mode:', mode, 'isEdit:', isEdit);
    
    const initializeComponent = async () => {
      await loadAvailableOptions();
      if (isEdit && id) {
        await loadBusinessUnit();
      } else {
        console.log('Setting pageLoading to false for new business unit');
        setPageLoading(false);
      }
    };
    
    initializeComponent();
  }, [id, mode]);

  const loadAvailableOptions = async () => {
    try {
      const settingsResult = await settingsService.getSettings();
      
      if (settingsResult.success && settingsResult.data) {
        const settings = settingsResult.data.data;
        setAvailableOptions({
          industries: (settings.industries || []).slice().sort((a, b) => a.localeCompare(b)),
          countries: settings.countries || [],
          regions: settings.regions || [],
          managingGroups: settings.managingGroups || [],
          departments: []
        });
      } else {
        console.error('Failed to load settings:', settingsResult.message);
        message.error('Failed to load available options');
      }
    } catch (error) {
      console.error('Error loading options:', error);
      message.error('Failed to load options');
    }
  };

  const loadBusinessUnit = async () => {
    if (!id || mode !== 'edit') {
      console.log('Prevented loadBusinessUnit - id:', id, 'mode:', mode);
      setPageLoading(false);
      return;
    }
    
    try {
      console.log('Loading business unit with id:', id);
      setPageLoading(true);
      const result = await businessUnitService.getBusinessUnitById(id);
      
      if (result.success && result.data) {
        const unit = result.data?.data;
        setBusinessUnit(unit);
        
        form.setFieldsValue({
          name: unit.name,
          industry: unit.industry,
          country: unit.country,
          region: unit.region,
          managingGroup: unit.managingGroup,
          departments: unit.departments || [],
          logo: unit.logo,
          backgroundColor: unit.backgroundColor || DEFAULT_QUIZ_BACKGROUND,
          textColor: unit.textColor || DEFAULT_TEXT_COLOR,
          backgroundImage: unit.backgroundImage || null,
          fontFamily: unit.fontFamily || DEFAULT_FONT_KEY,
          welcomeTextSize: unit.welcomeTextSize || 'md',
          questionTextSize: unit.questionTextSize || 'md',
          buttonColor: unit.buttonColor || null,
          buttonTextColor: unit.buttonTextColor || null,
          overrideQuestionnaireDesign:
            typeof unit.overrideQuestionnaireDesign === 'boolean'
              ? unit.overrideQuestionnaireDesign
              : false,
          notificationStrategy:
            Array.isArray(unit.notificationStrategy) && unit.notificationStrategy.length
              ? unit.notificationStrategy
              : DEFAULT_NOTIFICATION_STRATEGY
        });
      } else {
        message.error('Business unit not found');
        navigate('/dashboard/business-units');
      }
    } catch (error) {
      console.error('Error loading business unit:', error);
      message.error('Failed to load business unit');
      navigate('/dashboard/business-units');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const payload = {
        ...values,
        backgroundColor: values.backgroundColor || DEFAULT_QUIZ_BACKGROUND,
        backgroundImage: values.backgroundImage || null,
        textColor: values.textColor || DEFAULT_TEXT_COLOR,
        fontFamily: values.fontFamily || DEFAULT_FONT_KEY,
        welcomeTextSize: values.welcomeTextSize || 'md',
        questionTextSize: values.questionTextSize || 'md',
        buttonColor: values.buttonColor && values.buttonColor.trim() ? values.buttonColor : null,
        buttonTextColor: values.buttonTextColor && values.buttonTextColor.trim() ? values.buttonTextColor : null,
        overrideQuestionnaireDesign: Boolean(values.overrideQuestionnaireDesign),
        notificationStrategy:
          Array.isArray(values.notificationStrategy) && values.notificationStrategy.length
            ? values.notificationStrategy
            : DEFAULT_NOTIFICATION_STRATEGY
      };
      
      let result;
      if (isEdit) {
        result = await businessUnitService.updateBusinessUnit(id, payload);
      } else {
        result = await businessUnitService.createBusinessUnit(payload);
      }

      if (result.success) {
        message.success(`Business unit ${isEdit ? 'updated' : 'created'} successfully!`);
        navigate('/dashboard/business-units');
      } else {
        message.error(result.message || `Failed to ${isEdit ? 'update' : 'create'} business unit`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} business unit:`, error);
      message.error(`Failed to ${isEdit ? 'update' : 'create'} business unit`);
    } finally {
      setLoading(false);
    }
  };

  // Functions to handle adding new options
  const handleAddNew = (type) => {
    setAddModalType(type);
    setNewItemValue('');
    setAddModalVisible(true);
  };

  const handleAddNewItem = async () => {
    if (!newItemValue.trim()) {
      message.error('Please enter a value');
      return;
    }

    setAddingItem(true);
    try {
      let result;
      switch (addModalType) {
        case 'country':
          result = await settingsService.addCountry(newItemValue.trim());
          break;
        case 'region':
          result = await settingsService.addRegion(newItemValue.trim());
          break;
        case 'industry':
          result = await settingsService.addIndustry(newItemValue.trim());
          break;
        case 'managingGroup':
          result = await settingsService.addManagingGroup(newItemValue.trim());
          break;
        default:
          message.error('Invalid type');
          return;
      }

      if (result.success) {
        message.success(`${addModalType} added successfully!`);
        
        // Refresh available options
        await loadAvailableOptions();
        
        // Set the new value in the form
        const fieldName = addModalType === 'managingGroup' ? 'managingGroup' : addModalType;
        form.setFieldsValue({ [fieldName]: newItemValue.trim() });
        
        // Close modal
        setAddModalVisible(false);
        setNewItemValue('');
      } else {
        message.error(result.message || `Failed to add ${addModalType}`);
      }
    } catch (error) {
      console.error(`Error adding ${addModalType}:`, error);
      message.error(`Failed to add ${addModalType}`);
    } finally {
      setAddingItem(false);
    }
  };

  // Enhanced Select component with Add functionality
  const EnhancedSelect = ({ type, options, placeholder, allowClear = false, required = false, children, ...props }) => (
    <Space.Compact style={{ width: '100%' }}>
      <Select 
        {...props}
        placeholder={placeholder}
        size="large"
        allowClear={allowClear}
        style={{ width: '100%' }}
      >
        {options.map(option => (
          <Option key={option} value={option}>{option}</Option>
        ))}
      </Select>
      <Button
        size="large"
        icon={<PlusOutlined />}
        onClick={() => handleAddNew(type)}
        title={`Add new ${type}`}
        style={{ flexShrink: 0 }}
      />
    </Space.Compact>
  );

  if (pageLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  const breadcrumbs = isEdit 
    ? BREADCRUMB_PATTERNS.businessUnitEdit 
    : BREADCRUMB_PATTERNS.businessUnitCreate;

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title={isEdit ? 'Edit Organisation' : 'Create Organisation'}
        breadcrumbs={breadcrumbs}
      />

      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ margin: 0, color: '#2E86AB' }}>
                {isEdit ? `Edit ${businessUnit?.name || 'Organisation'}` : 'Create New Organisation'}
              </Title>
              <Text type="secondary">
                {isEdit ? 'Update organisation details and departments' : 'Fill in the details to create a new organisation'}
              </Text>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              disabled={loading}
              initialValues={{
                backgroundColor: DEFAULT_QUIZ_BACKGROUND,
                textColor: DEFAULT_TEXT_COLOR,
                fontFamily: DEFAULT_FONT_KEY,
                welcomeTextSize: 'md',
                questionTextSize: 'md',
                overrideQuestionnaireDesign: false,
                backgroundImage: null,
                notificationStrategy: DEFAULT_NOTIFICATION_STRATEGY
              }}
            >
              <Form.Item name="textColor" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="backgroundColor" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="backgroundImage" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="buttonColor" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="buttonTextColor" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Tabs
                defaultActiveKey="1"
                items={[
                  {
                    key: '1',
                    forceRender: true,
                    label: (
                      <span>
                        <InfoCircleOutlined />
                        Basic Information
                      </span>
                    ),
                    children: (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          <Form.Item
                            label="Organisation Name"
                            name="name"
                            rules={[{ required: true, message: 'Please enter organisation name' }]}
                          >
                            <Input placeholder="Enter organisation name" size="large" />
                          </Form.Item>

                          <Form.Item
                            label="Industry"
                            name="industry"
                            rules={[{ required: true, message: 'Please select industry' }]}
                          >
                            <EnhancedSelect
                              type="industry"
                              options={availableOptions.industries}
                              placeholder="Select industry"
                              required={true}
                            />
                          </Form.Item>

                          <Form.Item
                            label="Country"
                            name="country"
                            rules={[{ required: true, message: 'Please select country' }]}
                          >
                            <EnhancedSelect
                              type="country"
                              options={availableOptions.countries}
                              placeholder="Select country"
                              required={true}
                            />
                          </Form.Item>

                          <Form.Item
                            label="Region"
                            name="region"
                          >
                            <EnhancedSelect
                              type="region"
                              options={availableOptions.regions}
                              placeholder="Select region (optional)"
                              allowClear={true}
                            />
                          </Form.Item>

                          <Form.Item
                            label="Managing Group"
                            name="managingGroup"
                          >
                            <EnhancedSelect
                              type="managingGroup"
                              options={availableOptions.managingGroups}
                              placeholder="Select managing group (optional)"
                              allowClear={true}
                            />
                          </Form.Item>
                        </Space>
                      ),
                    },
                    {
                      key: 'notifications',
                      forceRender: true,
                      label: (
                        <span>
                          <BellOutlined />
                          Notification Strategy
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          <Form.Item
                            label="Channel order & fallbacks"
                            name="notificationStrategy"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (Array.isArray(value) && value.length) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(new Error('Select at least one channel'));
                                }
                              }
                            ]}
                            help="Channels fire in the order shown. Each failure rolls to the next channel."
                          >
                            <NotificationStrategySelector />
                          </Form.Item>
                        </Space>
                      )
                    },
                    {
                      key: '2',
                      forceRender: true,
                      label: (
                        <span>
                          <BuildOutlined />
                          Departments
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          <Form.Item
                            label="Departments"
                            name="departments"
                            help="Type department name and press Enter to add. Click tags to remove."
                          >
                            <Select
                              mode="tags"
                              style={{ width: '100%' }}
                              placeholder="Add departments..."
                              tokenSeparators={[',']}
                              size="large"
                              options={availableOptions.departments.map(dept => ({
                                label: dept,
                                value: dept
                              }))}
                              filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                              }
                              notFoundContent="Type to add new department"
                            />
                          </Form.Item>
                          
                          {/* Display current departments */}
                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const currentDepartments = getFieldValue('departments') || [];
                              if (currentDepartments.length > 0) {
                                return (
                                  <div>
                                    <Text type="secondary">
                                      Current Departments ({currentDepartments.length}):
                                    </Text>
                                    <div style={{ marginTop: 8 }}>
                                      {currentDepartments.map((dept, index) => (
                                        <span
                                          key={index}
                                          style={{
                                            display: 'inline-block',
                                            padding: '6px 12px',
                                            margin: '4px',
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            border: '1px solid #d9d9d9'
                                          }}
                                        >
                                          {dept}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          </Form.Item>
                        </Space>
                      ),
                    },
                    {
                      key: '3',
                      forceRender: true,
                      label: (
                        <span>
                          <BgColorsOutlined />
                          Appearance
                        </span>
                      ),
                      children: (
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          <Form.Item
                            label="Organisation Logo"
                            name="logo"
                            help="Upload your organisation logo (will be compressed automatically)"
                          >
                            <Form.Item noStyle shouldUpdate>
                              {({ getFieldValue, setFieldsValue }) => {
                                const currentLogo = getFieldValue('logo');

                                const handleImageUpload = async (file, callback) => {
                                  try {
                                    const base64 = await fileToBase64(file, 1024);
                                    callback(base64);
                                    setFieldsValue({ logo: base64 });
                                    message.success('Logo uploaded successfully');
                                  } catch (error) {
                                    message.error('Failed to upload logo: ' + error.message);
                                  }
                                };

                                return (
                                  <div>
                                    <Upload
                                      accept="image/*"
                                      showUploadList={false}
                                      customRequest={({ file, onSuccess }) =>
                                        handleImageUpload(file, () => {
                                          onSuccess();
                                        })
                                      }
                                    >
                                      <Button icon={<UploadOutlined />} size="large">
                                        {currentLogo ? 'Change Logo' : 'Upload Logo'}
                                      </Button>
                                    </Upload>
                                    {currentLogo && (
                                      <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                        <Image src={currentLogo} width={150} style={{ borderRadius: '8px' }} />
                                        <br />
                                        <Button
                                          type="link"
                                          size="small"
                                          onClick={() => setFieldsValue({ logo: null })}
                                          style={{ marginTop: '8px' }}
                                        >
                                          Remove Logo
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              }}
                            </Form.Item>
                          </Form.Item>

                          <Form.Item
                            label="Theme source"
                            name="overrideQuestionnaireDesign"
                            valuePropName="checked"
                            tooltip="Switch on to force quizzes to inherit this organization&apos;s appearance"
                            help="On = use this organization&apos;s colors, fonts, and buttons for every related quiz. Off = respect each quiz designer&apos;s theme."
                          >
                            <Switch
                              checkedChildren="Organization design"
                              unCheckedChildren="Quiz design"
                            />
                          </Form.Item>

                          <Card
                            size="small"
                            bodyStyle={{ padding: '16px 12px 4px' }}
                            style={{ borderColor: '#e4e9f2' }}
                          >
                            <Tabs
                              activeKey={appearanceDesignTab}
                              onChange={setAppearanceDesignTab}
                              items={[
                              {
                                key: 'font',
                                label: 'Font',
                                children: (
                                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Form.Item
                                      label="Font Family"
                                      name="fontFamily"
                                      help="Applies to quizzes, results, and any participant-facing content"
                                    >
                                      <Select
                                        placeholder="Select a font"
                                        size="large"
                                        options={FONT_SELECT_OPTIONS}
                                      />
                                    </Form.Item>

                                    <Form.Item label="Title & Question Color" shouldUpdate>
                                      {({ getFieldValue, setFieldsValue }) => {
                                        const currentTextColor = getFieldValue('textColor') || DEFAULT_TEXT_COLOR;
                                        return (
                                          <Row gutter={8}>
                                            <Col span={16}>
                                              <Input
                                                value={currentTextColor}
                                                onChange={(e) => setFieldsValue({ textColor: e.target.value })}
                                                placeholder="CSS color value"
                                                size="large"
                                              />
                                            </Col>
                                            <Col span={8}>
                                              <ColorPicker
                                                value={currentTextColor}
                                                onChange={(color) => setFieldsValue({ textColor: color.toHexString() })}
                                                style={{ width: '100%' }}
                                              />
                                            </Col>
                                          </Row>
                                        );
                                      }}
                                    </Form.Item>

                                    <Row gutter={24}>
                                      <Col span={12}>
                                        <Form.Item
                                          label="Welcome screen & results size"
                                          name="welcomeTextSize"
                                        >
                                          <Segmented options={TEXT_SIZE_SEGMENT_OPTIONS} block />
                                        </Form.Item>
                                      </Col>
                                      <Col span={12}>
                                        <Form.Item
                                          label="Question text size"
                                          name="questionTextSize"
                                        >
                                          <Segmented options={TEXT_SIZE_SEGMENT_OPTIONS} block />
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  </Space>
                                )
                              },
                              {
                                key: 'buttons',
                                label: 'Buttons',
                                children: (
                                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Form.Item label="Button Color" shouldUpdate>
                                      {({ getFieldValue, setFieldsValue }) => {
                                        const currentButtonColor = getFieldValue('buttonColor') || '';
                                        const fallback = getFieldValue('textColor') || DEFAULT_TEXT_COLOR;
                                        const pickerValue = currentButtonColor && currentButtonColor.startsWith('#')
                                          ? currentButtonColor
                                          : fallback;
                                        return (
                                          <>
                                            <Row gutter={8}>
                                              <Col span={16}>
                                                <Input
                                                  value={currentButtonColor || ''}
                                                  onChange={(e) => {
                                                    const nextValue = e.target.value;
                                                    setFieldsValue({ buttonColor: nextValue ? nextValue : null });
                                                  }}
                                                  placeholder="CSS color value"
                                                  addonBefore="Button"
                                                  size="large"
                                                />
                                              </Col>
                                              <Col span={8}>
                                                <ColorPicker
                                                  value={pickerValue}
                                                  onChange={(color) => setFieldsValue({ buttonColor: color.toHexString() })}
                                                  style={{ width: '100%' }}
                                                />
                                              </Col>
                                            </Row>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                              Leave blank to inherit the text color ({fallback}).
                                            </Text>
                                          </>
                                        );
                                      }}
                                    </Form.Item>

                                    <Form.Item label="Button Text Color" shouldUpdate>
                                      {({ getFieldValue, setFieldsValue }) => {
                                        const currentButtonTextColor = getFieldValue('buttonTextColor') || '';
                                        const pickerValue = currentButtonTextColor && currentButtonTextColor.startsWith('#')
                                          ? currentButtonTextColor
                                          : '#ffffff';
                                        return (
                                          <>
                                            <Row gutter={8}>
                                              <Col span={16}>
                                                <Input
                                                  value={currentButtonTextColor || ''}
                                                  onChange={(e) => {
                                                    const nextValue = e.target.value;
                                                    setFieldsValue({ buttonTextColor: nextValue ? nextValue : null });
                                                  }}
                                                  placeholder="CSS color value"
                                                  addonBefore="Button Text"
                                                  size="large"
                                                />
                                              </Col>
                                              <Col span={8}>
                                                <ColorPicker
                                                  value={pickerValue}
                                                  onChange={(color) => setFieldsValue({ buttonTextColor: color.toHexString() })}
                                                  style={{ width: '100%' }}
                                                />
                                              </Col>
                                            </Row>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                              Leave blank to auto-detect a readable contrast.
                                            </Text>
                                          </>
                                        );
                                      }}
                                    </Form.Item>
                                  </Space>
                                )
                              },
                              {
                                key: 'background',
                                label: 'Background',
                                children: (
                                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                    <Form.Item label="Background Color or Gradient" shouldUpdate>
                                      {({ getFieldValue, setFieldsValue }) => {
                                        const currentBg = getFieldValue('backgroundColor') || DEFAULT_QUIZ_BACKGROUND;
                                        return (
                                          <div>
                                            <Row gutter={8}>
                                              <Col span={16}>
                                                <Input
                                                  value={currentBg}
                                                  onChange={(e) => setFieldsValue({ backgroundColor: e.target.value })}
                                                  placeholder="CSS color value or gradient"
                                                  size="large"
                                                />
                                              </Col>
                                              <Col span={8}>
                                                <ColorPicker
                                                  value={currentBg.startsWith('#') ? currentBg : '#52c41a'}
                                                  onChange={(color) => setFieldsValue({ backgroundColor: color.toHexString() })}
                                                  style={{ width: '100%' }}
                                                />
                                              </Col>
                                            </Row>
                                            <div style={{ marginTop: '12px' }}>
                                              <Space size="small">
                                                <Button
                                                  size="small"
                                                  onClick={() => setFieldsValue({ backgroundColor: DEFAULT_QUIZ_BACKGROUND })}
                                                  style={{
                                                    background: DEFAULT_QUIZ_BACKGROUND,
                                                    color: '#fff',
                                                    border: 'none'
                                                  }}
                                                >
                                                  Green
                                                </Button>
                                                <Button
                                                  size="small"
                                                  onClick={() => setFieldsValue({ backgroundColor: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)' })}
                                                  style={{
                                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                    color: '#fff',
                                                    border: 'none'
                                                  }}
                                                >
                                                  Blue
                                                </Button>
                                                <Button
                                                  size="small"
                                                  onClick={() => setFieldsValue({ backgroundColor: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)' })}
                                                  style={{
                                                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                                    color: '#fff',
                                                    border: 'none'
                                                  }}
                                                >
                                                  Purple
                                                </Button>
                                              </Space>
                                            </div>
                                          </div>
                                        );
                                      }}
                                    </Form.Item>

                                    <Form.Item label="Background Image" shouldUpdate>
                                      {({ getFieldValue, setFieldsValue }) => {
                                        const currentImage = getFieldValue('backgroundImage');
                                        return (
                                          <div>
                                            <Upload
                                              accept="image/*"
                                              showUploadList={false}
                                              customRequest={async ({ file, onSuccess, onError }) => {
                                                try {
                                                  const base64 = await fileToBase64(file, 2048);
                                                  setFieldsValue({ backgroundImage: base64 });
                                                  message.success('Background image updated');
                                                  onSuccess();
                                                } catch (error) {
                                                  console.error('Background upload failed', error);
                                                  message.error('Failed to process background image');
                                                  onError?.(error);
                                                }
                                              }}
                                            >
                                              <Button icon={<UploadOutlined />} size="large" block>
                                                {currentImage ? 'Change Background Image' : 'Upload Background Image'}
                                              </Button>
                                            </Upload>
                                            {currentImage && (
                                              <div style={{ marginTop: '12px', textAlign: 'center' }}>
                                                <Image src={currentImage} width={160} style={{ borderRadius: '8px' }} />
                                                <br />
                                                <Button
                                                  type="link"
                                                  size="small"
                                                  onClick={() => setFieldsValue({ backgroundImage: null })}
                                                >
                                                  Remove image
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      }}
                                    </Form.Item>
                                  </Space>
                                )
                              }
                            ]}
                            />
                          </Card>

                          <Form.Item noStyle shouldUpdate>
                            {({ getFieldValue }) => {
                              const bgColor = getFieldValue('backgroundColor') || DEFAULT_QUIZ_BACKGROUND;
                              const bgImage = getFieldValue('backgroundImage');
                              const textColor = getFieldValue('textColor') || DEFAULT_TEXT_COLOR;
                              const orgName = getFieldValue('name') || 'Organization Name';
                              const fontFamily = getFieldValue('fontFamily') || DEFAULT_FONT_KEY;
                              const welcomeSize = getFieldValue('welcomeTextSize') || 'md';
                              const questionSize = getFieldValue('questionTextSize') || 'md';
                              const buttonColor = getFieldValue('buttonColor');
                              const buttonTextColor = getFieldValue('buttonTextColor');

                              const previewFontFamily = getFontStack(fontFamily);
                              const previewWelcomeSize = getTextSizePx('welcome', welcomeSize);
                              const previewQuestionSize = getTextSizePx('question', questionSize);
                              const previewAnswerSize = getTextSizePx('answer', questionSize);
                              const fallbackButtonColor = textColor || DEFAULT_TEXT_COLOR;
                              const previewButtonColor = buttonColor && buttonColor.trim() ? buttonColor : fallbackButtonColor;
                              const previewButtonTextColor = buttonTextColor && buttonTextColor.trim()
                                ? buttonTextColor
                                : getReadableTextColor(previewButtonColor, '#000000');
                              const previewBackgroundStyles = bgImage
                                ? {
                                    backgroundImage: `linear-gradient(135deg, rgba(0,0,0,0.55), rgba(0,0,0,0.35)), url(${bgImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat'
                                  }
                                : {
                                    background: bgColor
                                  };

                              return (
                                <div>
                                  <Text strong style={{ display: 'block', marginBottom: '12px' }}>Preview</Text>
                                  <div
                                    style={{
                                      ...previewBackgroundStyles,
                                      color: textColor,
                                      padding: '28px',
                                      borderRadius: '12px',
                                      textAlign: 'center',
                                      fontFamily: previewFontFamily,
                                      boxShadow: '0 12px 30px rgba(0,0,0,0.12)'
                                    }}
                                  >
                                    <Title
                                      level={4}
                                      style={{
                                        color: textColor,
                                        marginBottom: '8px',
                                        fontFamily: previewFontFamily,
                                        fontSize: `${previewWelcomeSize}px`
                                      }}
                                    >
                                      {orgName}
                                    </Title>
                                    <Text
                                      style={{
                                        color: textColor,
                                        opacity: 0.9,
                                        fontSize: `${previewQuestionSize}px`,
                                        display: 'block',
                                        marginBottom: '20px'
                                      }}
                                    >
                                      Participant-facing copy will follow these typography rules.
                                    </Text>
                                    <div style={{ marginBottom: '20px', textAlign: 'left' }}>
                                      <Text strong style={{ color: textColor, display: 'block', marginBottom: '8px' }}>
                                        Sample Question
                                      </Text>
                                      <Text style={{ color: textColor, opacity: 0.85, display: 'block', fontSize: `${previewAnswerSize}px`, marginBottom: '4px' }}>
                                        • Option one answer text
                                      </Text>
                                      <Text style={{ color: textColor, opacity: 0.85, display: 'block', fontSize: `${previewAnswerSize}px` }}>
                                        • Option two answer text
                                      </Text>
                                    </div>
                                    <Button
                                      style={{
                                        backgroundColor: previewButtonColor,
                                        borderColor: previewButtonColor,
                                        color: previewButtonTextColor,
                                        fontFamily: previewFontFamily,
                                        fontWeight: 600,
                                        fontSize: `${previewAnswerSize}px`,
                                        padding: '0 32px',
                                        height: '44px'
                                      }}
                                    >
                                      Start Quiz
                                    </Button>
                                  </div>
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Space>
                      ),
                    }
                  ]}
                />

                <Form.Item style={{ marginTop: '30px', marginBottom: 0 }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button 
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate('/dashboard/business-units')}
                      size="large"
                    >
                      Back to Organizations
                    </Button>
                    
                    <Button
                      
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                      size="large"
                      style={{ 
                        background: '#2E86AB',
                        borderColor: '#2E86AB'
                      }}
                    >
                      {isEdit ? 'Update' : 'Create'} Organization
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </div>

      {/* Modal for adding new options */}
      <Modal
        title={`Add New ${addModalType.charAt(0).toUpperCase() + addModalType.slice(1)}`}
        open={addModalVisible}
        onOk={handleAddNewItem}
        onCancel={() => {
          setAddModalVisible(false);
          setNewItemValue('');
        }}
        okButtonProps={{ loading: addingItem ,type: 'default'}}
        confirmLoading={addingItem}
        okText="Add"
        cancelText="Cancel"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>Enter the new {addModalType} name:</Text>
          <Input
            placeholder={`Enter ${addModalType} name`}
            value={newItemValue}
            onChange={(e) => setNewItemValue(e.target.value)}
            onPressEnter={handleAddNewItem}
            autoFocus
          />
        </Space>
      </Modal>
    </div>
  );
};

export default EditBusinessUnit;